import { gql } from './client';
import { isAllowedImageSrc } from './richtext';

/** What a platform picker hands back. */
export interface PickedImage {
  /** Local file URI (RN) or an object URL (web). */
  uri: string;
  contentType: string;
  byteSize: number;
  width?: number;
  height?: number;
  fileName?: string;
  /** Web supplies the File directly; RN uploads by URI. */
  blob?: Blob;
}

/**
 * Chooses an image. Injected per platform, the same way sounds are: the
 * shared screens must not depend on expo-image-picker or on a DOM file input.
 * Returns null when the user cancels.
 */
export type ImagePicker = () => Promise<PickedImage | null>;

let picker: ImagePicker | null = null;

export function setImagePicker(fn: ImagePicker | null): void {
  picker = fn;
}

export function canPickImages(): boolean {
  return picker !== null;
}

export function pickImage(): Promise<PickedImage | null> {
  if (!picker) {
    return Promise.reject(
      new Error('Choosing images is not available in this build.')
    );
  }
  return picker();
}

const M_CREATE_UPLOAD = `mutation CreateUpload($contentType: String!, $byteSize: Int!) {
  createUpload(contentType: $contentType, byteSize: $byteSize) {
    url
    fields
    key
    publicUrl
    expiresAt
  }
}`;

interface UploadTicket {
  url: string;
  /**
   * Form fields from the presigned POST policy. AWSJSON reaches the client as
   * a JSON string, but tolerate an object too so a change at the resolver
   * cannot silently corrupt the form.
   */
  fields: string | Record<string, string>;
  key: string;
  publicUrl: string;
  expiresAt: number;
}

/** Images larger than this are rejected before a byte leaves the device. */
export const MAX_IMAGE_BYTES = 5 * 1024 * 1024;

export const ALLOWED_IMAGE_TYPES = ['image/png', 'image/jpeg', 'image/gif', 'image/webp'];

/**
 * Uploads a picked image and returns the URL to reference in a body.
 *
 * The file goes straight from the device to S3 with a presigned POST — it
 * never passes through AppSync or a Lambda, so a 5 MB image costs one small
 * mutation rather than 5 MB of function payload.
 */
export async function uploadImage(image: PickedImage): Promise<string> {
  if (!ALLOWED_IMAGE_TYPES.includes(image.contentType)) {
    throw new Error('Images must be PNG, JPEG, GIF, or WebP.');
  }
  if (image.byteSize > MAX_IMAGE_BYTES) {
    throw new Error('Images must be smaller than 5 MB.');
  }

  const { createUpload: ticket } = await gql<{ createUpload: UploadTicket }>(
    M_CREATE_UPLOAD,
    { contentType: image.contentType, byteSize: image.byteSize }
  );

  const fields =
    typeof ticket.fields === 'string'
      ? (JSON.parse(ticket.fields) as Record<string, string>)
      : ticket.fields;
  if (typeof fields !== 'object' || fields === null || Array.isArray(fields)) {
    throw new Error('The upload ticket was malformed.');
  }

  const form = new FormData();
  for (const [k, v] of Object.entries(fields)) {
    form.append(k, String(v));
  }
  // The file part must come last: S3 ignores anything after it.
  if (image.blob) {
    // The DOM's FormData takes a filename as a third argument; React Native's
    // type declarations omit it. The web path is the only one that reaches
    // here, and browsers honour it, so the cast is safe.
    (form.append as (k: string, v: Blob, filename?: string) => void)(
      'file',
      image.blob,
      image.fileName ?? 'image'
    );
  } else {
    // React Native's FormData takes a {uri, type, name} descriptor.
    form.append('file', {
      uri: image.uri,
      type: image.contentType,
      name: image.fileName ?? 'image',
    } as unknown as Blob);
  }

  let res: Response;
  try {
    res = await fetch(ticket.url, { method: 'POST', body: form });
  } catch (err) {
    throw new Error('The image could not be uploaded. Check your connection.');
  }

  if (!res.ok) {
    // S3 explains policy violations in an XML body; surface its error code,
    // because "400" alone is not enough to debug an upload.
    const detail = await res.text().catch(() => '');
    const code = /<Code>([^<]+)<\/Code>/.exec(detail)?.[1];
    throw new Error(
      res.status === 403
        ? 'The upload was rejected — the image may be too large.'
        : `The image could not be uploaded (${res.status}${code ? `: ${code}` : ''}).`
    );
  }

  if (!isAllowedImageSrc(ticket.publicUrl)) {
    // Would mean the configured uploads origin and the backend disagree.
    throw new Error('The upload succeeded but its URL is not a trusted origin.');
  }

  return ticket.publicUrl;
}
